import type { AbsenceReason } from './attendance'

/**
 * Who has told the studio they will be missing (D-188).
 *
 * The screen that did not exist. Every field here has been stored since the
 * parent portal shipped and shown nowhere — which is also what quietly undid
 * D-122's safeguard, since that decision answered "a family could excuse
 * everything" by claiming a pattern would be visible.
 */
export type ReportedAbsence = {
  attendanceRecordId: string
  dancerId: string
  dancerName: string
  occurrenceId: string
  studioClassId: string
  className: string
  date: string
  startTime: string | null
  reason: AbsenceReason | null
  reportedBy: string | null
  reportedAt: string | null
  reportedNote: string | null
  /** A note is on file. Said to everyone who may read the list. */
  hasDocument: boolean
  /**
   * The note itself, openable (D-213). Null unless the caller may read one —
   * omitted from the payload rather than hidden here (D-9).
   */
  documentId: string | null
  /**
   * Null for anyone who may not see excusals — omitted from the payload
   * entirely rather than hidden by the UI.
   */
  isExcused: boolean | null
  excusedNote: string | null
}

export type ReportedAbsenceList = {
  absences: ReportedAbsence[]
  canExcuse: boolean
  isStudioWide: boolean
  /**
   * When this person last opened the screen, as it was *before* this request
   * (D-211). Null means they never have, and everything is new.
   */
  seenAt: string | null
}

export async function fetchReportedAbsences(
  from?: string,
  to?: string,
): Promise<ReportedAbsenceList> {
  const params = new URLSearchParams()
  if (from) params.set('from', from)
  if (to) params.set('to', to)

  const res = await fetch(`/api/attendance/reported-absences?${params}`)
  if (!res.ok) throw new Error(`Could not load the absences (${res.status})`)

  return res.json()
}

/**
 * Records that this person has now looked (D-211).
 *
 * <b>Per account, never shared.</b> The list is already scoped — a teacher sees
 * her own classes and Tonya the studio (D-83, D-188) — so a shared marker would
 * let Tonya clear the badge for every teacher, about reports she was never shown.
 */
export async function markAbsencesSeen(): Promise<void> {
  const res = await fetch('/api/attendance/reported-absences/seen', { method: 'POST' })
  if (!res.ok) throw new Error(`Could not update that (${res.status})`)
}

/**
 * How many of these arrived since this person last looked.
 *
 * <b>The badge used to count every absence ever reported</b>, so it only grew —
 * by March it reads sixty and means nothing, which is the warning that always
 * fires and is therefore never read (D-149). Zero here genuinely means "nothing
 * new", rather than being a convention the screen has agreed to.
 *
 * A report with no `reportedAt` cannot be newer than anything, so it never
 * counts — that is data from before the field existed, not something that just
 * came in.
 */
export function newSince(absences: ReportedAbsence[], seenAt: string | null): ReportedAbsence[] {
  if (seenAt === null) return absences.filter((a) => a.reportedAt !== null)

  const seen = Date.parse(seenAt)
  if (Number.isNaN(seen)) return []

  return absences.filter((a) => {
    if (a.reportedAt === null) return false

    const reported = Date.parse(a.reportedAt)
    return !Number.isNaN(reported) && reported > seen
  })
}

/** Whether one row arrived since the last visit, for the "New" marker. */
export function isNew(absence: ReportedAbsence, seenAt: string | null): boolean {
  return newSince([absence], seenAt).length > 0
}

export async function excuseAbsence(id: string, note: string | null): Promise<void> {
  const res = await fetch(`/api/attendance/reported-absences/${id}/excuse`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note }),
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.title ?? `Could not excuse that (${res.status})`)
  }
}

/**
 * Forgiving a dancer's whole day at once (D-213).
 *
 * A fever misses every class that evening, and that is one decision rather than
 * three. The note describes the day, so it is written to all of them.
 */
export async function excuseAbsences(ids: string[], note: string | null): Promise<void> {
  const res = await fetch('/api/attendance/reported-absences/excuse', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attendanceRecordIds: ids, note }),
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.title ?? `Could not excuse those (${res.status})`)
  }
}

export async function withdrawExcusal(id: string): Promise<void> {
  const res = await fetch(`/api/attendance/reported-absences/${id}/excuse`, { method: 'DELETE' })
  if (!res.ok) throw new Error(`Could not undo that (${res.status})`)
}

/** Where a doctor's note is served from. Gated and audited server-side (D-9). */
export function documentHref(documentId: string): string {
  return `/api/portal/documents/${documentId}`
}

/** Which absences the screen is showing. Everything, by default (D-197). */
export type AbsenceWhen = 'all' | 'upcoming' | 'past'

/**
 * Absences grouped by the date they fall on.
 *
 * A flat list of forty rows spanning a season is unreadable; "who is out on
 * Thursday" is the question actually being asked, and it is answered by the
 * shape of the page rather than by a filter somebody has to set.
 *
 * **Upcoming first, soonest at the top; past below, most recent first.**
 * Everything is shown by default (D-197) — a parent reporting a holiday two
 * months out was invisible before — but "everything" ordered purely by date
 * would open on last month and push the thing to act on off the screen.
 */
export function byDate(
  absences: ReportedAbsence[],
  today: string,
): { date: string; absences: ReportedAbsence[]; isPast: boolean }[] {
  const groups = new Map<string, ReportedAbsence[]>()

  for (const absence of absences) {
    const existing = groups.get(absence.date)
    if (existing) existing.push(absence)
    else groups.set(absence.date, [absence])
  }

  const days = [...groups.entries()].map(([date, rows]) => ({
    date,
    absences: rows,
    isPast: date < today,
  }))

  const upcoming = days.filter((d) => !d.isPast).sort((a, b) => a.date.localeCompare(b.date))
  const past = days.filter((d) => d.isPast).sort((a, b) => b.date.localeCompare(a.date))

  return [...upcoming, ...past]
}

/**
 * Narrows the list without going back to the server.
 *
 * The endpoint returns everything because a report is one row per session a
 * family actually mentioned — a tiny fraction of a season. Filtering here keeps
 * switching instant and means the counts beside each option are truthful rather
 * than guesses about what another request would return.
 */
export function inWindow(
  absences: ReportedAbsence[],
  when: AbsenceWhen,
  today: string,
): ReportedAbsence[] {
  if (when === 'all') return absences
  if (when === 'upcoming') return absences.filter((a) => a.date >= today)
  return absences.filter((a) => a.date < today)
}

/**
 * Narrows the list to one dancer, or one class (D-412).
 *
 * <b>Reported:</b> *"this page should be searchable. so if a parent comes to
 * tonya and asks about their kids absenses she should be able to pull it up
 * and see the kids attendence."* Until this the only filters were three time
 * windows, so answering a parent at the desk meant scrolling the studio.
 *
 * <b>Both names, because both are how she would be asked.</b> A parent says a
 * child's name; a teacher says a class. Matching the class name as well costs
 * nothing and answers "who is out of Tuesday ballet" at the same time.
 *
 * <b>Every word has to appear, in either field.</b> Typing "ellie tap" finds
 * Ellie's tap absences rather than everything matching either word, which is
 * what a single `includes` on the joined string would do. Order does not
 * matter, so "tap ellie" is the same search.
 */
export function matchesSearch(absence: ReportedAbsence, search: string): boolean {
  const words = search.toLowerCase().split(/\s+/).filter(Boolean)
  if (words.length === 0) return true

  const haystack = `${absence.dancerName} ${absence.className}`.toLowerCase()

  return words.every((word) => haystack.includes(word))
}

/**
 * How many separate children are in this list.
 *
 * <b>Dancers, not rows.</b> One girl reported out of three Thursday classes is
 * one child missing, and a heading saying "3 dancers" would send somebody
 * looking for two who do not exist.
 */
export function distinctDancers(absences: ReportedAbsence[]): number {
  return new Set(absences.map((a) => a.dancerId)).size
}

/**
 * Dancers out on more than one <em>day</em> in this window.
 *
 * The pattern D-122 assumed somebody would notice and gave nobody a way to see.
 * Surfaced as a count per dancer rather than a judgement — what follows is a
 * conversation, not a rule.
 *
 * <b>Days, not rows (D-213).</b> Counting rows made a single sick Tuesday with
 * three classes on it read as "missing more than once", so the box fired for
 * nearly every dancer who missed anything — and it is the box somebody would
 * use to decide whether to ring a family. A warning that always fires is one
 * nobody reads (D-149), and this one was worse than noise because it was wrong.
 */
export function repeatedAbsences(
  absences: ReportedAbsence[],
): { dancerId: string; dancerName: string; count: number }[] {
  const days = new Map<string, { dancerName: string; dates: Set<string> }>()

  for (const absence of absences) {
    const existing = days.get(absence.dancerId)
    if (existing) existing.dates.add(absence.date)
    else days.set(absence.dancerId, { dancerName: absence.dancerName, dates: new Set([absence.date]) })
  }

  const counts = new Map<string, { dancerName: string; count: number }>(
    [...days.entries()].map(([id, v]) => [id, { dancerName: v.dancerName, count: v.dates.size }]),
  )

  return [...counts.entries()]
    .filter(([, v]) => v.count > 1)
    .map(([dancerId, v]) => ({ dancerId, dancerName: v.dancerName, count: v.count }))
    .sort((a, b) => b.count - a.count || a.dancerName.localeCompare(b.dancerName))
}

/**
 * One dancer's absences on one day, as the screen lists them (D-213).
 *
 * <b>The unit somebody actually thinks in.</b> "Who is out on Tuesday" is the
 * question; "which sessions have a report attached" is not. Ellie missing three
 * classes with a fever was three cards, so ten dancers out on a busy evening
 * would have been thirty — a page of rows describing ten children.
 */
export type DancerDay = {
  dancerId: string
  dancerName: string
  date: string
  absences: ReportedAbsence[]
  /** The distinct reasons given, so a day with two of them can say so. */
  reasons: AbsenceReason[]
  /** How many are already forgiven. Null for anyone who may not see excusals. */
  excusedCount: number | null
  /** A doctor's note is attached to at least one of them. */
  hasDocument: boolean
}

/**
 * Groups a day's absences by dancer, keeping the order they arrived in.
 *
 * The rows are already sorted by time, so the first class a dancer misses
 * decides where she sits in the day — which is the order somebody reading down
 * an evening expects.
 */
export function byDancer(absences: ReportedAbsence[]): DancerDay[] {
  const groups = new Map<string, ReportedAbsence[]>()

  for (const absence of absences) {
    const key = `${absence.dancerId}|${absence.date}`
    const existing = groups.get(key)

    if (existing) existing.push(absence)
    else groups.set(key, [absence])
  }

  return [...groups.values()].map((rows) => ({
    dancerId: rows[0].dancerId,
    dancerName: rows[0].dancerName,
    date: rows[0].date,
    absences: rows,
    reasons: [...new Set(rows.map((r) => r.reason).filter((r): r is AbsenceReason => r !== null))],

    /*
     * Null rather than zero when the caller may not see excusals. The field is
     * absent from those payloads entirely (D-188), and counting absent fields
     * as "none excused" would state something this caller was never told.
     */
    excusedCount: rows.some((r) => r.isExcused === null)
      ? null
      : rows.filter((r) => r.isExcused === true).length,

    hasDocument: rows.some((r) => r.hasDocument),
  }))
}

/**
 * The classes in a dancer's day that are not already forgiven.
 *
 * What "don't count any of these" should actually send — re-excusing one that
 * is already excused would rewrite its note with the new one, quietly replacing
 * a reason somebody typed earlier.
 */
export function stillCounting(day: DancerDay): ReportedAbsence[] {
  return day.absences.filter((a) => a.isExcused !== true)
}
